import { Formik, FormikConfig } from "formik";
import React, { createContext, PropsWithChildren, useContext } from "react";
import { createPortal } from "react-dom";

import useApi from "../../../hooks/useApi";
import Icon from "../icons/Icon";
import "./ModalForm.css";

export interface DynamicDialogProps {
  close: () => void;
}

interface DialogProps {
  trigger: React.ReactElement;
  dialog: React.ReactElement | ((close: () => void) => React.ReactElement);
  visible?: boolean;
}


interface DialogContextType {
  setVisible: (visible: boolean) => void;
}

const DialogContext = createContext<DialogContextType | null>(null);

const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error("useDialog must be used within a Dialog component");
  }
  return context;
};

function Portal({ children }: { children: React.ReactNode }) {
  return createPortal(children, document.body);
}

export function Dialog(props: DialogProps) {

  const [visible, setVisible] = React.useState<boolean>(!!props.visible);

  return (
    <>
      {
        props.trigger && React.cloneElement(props.trigger, { onClick: () => setVisible(true) })
      }
      {
        visible && (
          <Portal>
            <DialogContext.Provider value={{ setVisible }}>
              {
                typeof props.dialog === "function"
                  ? props.dialog(() => setVisible(false))
                  : props.dialog
              }
            </DialogContext.Provider>
          </Portal>
        )
      }
    </>
  )
}

interface ModalFormProps<T> extends Omit<FormikConfig<T>, "onSubmit"> {
  onSubmit: (values: T, callApi: ReturnType<typeof useApi>) => Promise<boolean> | boolean;
  title: string;
  description: string;
}

export function ModalForm<T>(props: ModalFormProps<T>) {

  const { setVisible } = useDialog();

  const callApi = useApi();

  return (
    <DialogContent>
      <DialogHeader title={props.title} description={props.description} close={() => setVisible(false)} />
      <Formik
        {...props}
        onSubmit={async (values: any) => {
          const res = await props.onSubmit(values, callApi);
          if (!res) {
            return;
          }
          setVisible(false);
        }}
      />
    </DialogContent>
  )
}


export function DialogContent(props: PropsWithChildren<any>) {
  return (
    <div className="dialog d-flex align-items-center justify-content-center">
      <div className="dialog-content overflow-hidden d-flex flex-column">
        <div className="h-100 w-100 d-flex flex-column">
          {props.children}
        </div>
      </div>
    </div>
  )
}

interface DialogHeaderProps {
  close?: () => void;
  title: string;
  description: string;
}

export function DialogHeader(props: DialogHeaderProps) {

  const {
    close
  } = props;

  return (
    <div className="d-flex flex-row w-100 align-items-start justify-content-between dialog-header p-3 ps-4 pe-4">
      <div className="d-flex flex-column gap-0">
        <strong className="fs-5">{props.title}</strong>
        <span
          className="font-code"
          style={{
            fontSize: "10.5px",
            color: "var(--dark)"
          }}
        >{props.description}</span>
      </div>
      {
        close && (
          <Icon
            icon="x"
            color="var(--dark)"
            size={20}
            onClick={close}
          />
        )
      }
    </div>
  )
}


export function DialogBody(props: PropsWithChildren<any>) {
  const { children } = props;

  return (
    <div className="p-3 pe-4 ps-4 d-flex flex-column gap-3 overflow-y-auto dialog-body" style={{ flexGrow: 1, minHeight: 0 }}>
      {children}
    </div>
  )
}

export function DialogFooter(props: PropsWithChildren<any>) {

  const { children } = props;

  return (
    <div className="p-3 ps-4 pe-4 w-100 dialog-footer justify-content-end d-flex w-100">
      {children}
    </div>
  )
}

